import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@project/hooks/useAuth";

import {
  supabase,
  dbBool,
  dbNullableText,
  dbNumber,
  dbNumberList,
  dbText,
} from "@project/lib/data-client";
import { publicDonationSchemeService } from "@project/services/donate/donation-scheme.service";
import { publicDonationDonorService } from "@project/services/donate/donation-donor.service";
import { schemeToCheckoutCause } from "@project/donate/lib/scheme-mappers";
import { PageShell } from "@project/user/components/page-shell";
import generalDonationImage from "@project/assets/hero-jivandhara-family.webp";
import { PaymentInfoCard } from "@project/donate/components/payment-info-card";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@project/components/ui/radio-group";
import {
  DonorFields,
  // PaymentFields, // offline payment UI temporarily hidden
} from "@project/donate/components/donor-fields";
import {
  emptyDonor,
  emptyPayment,
  type DonorInfo,
  type PaymentInfo,
} from "@project/donate/components/donor-types";
import { MobileNumberField } from "@project/components/mobile-number-field";
import {
  DEFAULT_PHONE_COUNTRY,
  isValidMobileNumber,
  sanitizeMobileNumber,
  type PhoneCountryCode,
} from "@project/lib/mobile-number";
import {
  buildPublicCheckoutDonorCreate,
  buildPublicCheckoutDonorPatch,
  mapDonorRecordToDonorInfo,
} from "@project/donate/components/donor-utils";
import {
  validateCheckoutDonorDetails,
  type DonorFieldErrors,
} from "@project/donate/components/donor-validation";
import type { DonationDonorRecord } from "@project/types/donation-donor";
import { cn } from "@project/lib/utils";
import {
  Heart,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Shield,
  Award,
  Users,
  Loader2,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { useCampaignRazorpayPayment } from "@project/donate/components/razorpay-checkout";
import {
  DonationLocationFields,
  type DonationLocationValue,
} from "@project/donate/components/donation-location-fields";
import {
  PaymentVerificationOverlay,
  type PaymentVerificationStatus,
} from "@project/donate/components/payment-verification-overlay";

type Kind =
  | "campaign"
  | "general"
  | "type"
  | "category"
  | "infrastructure"
  | "seva"
  | "event";

const inr = (n: number) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const FREQUENCIES = ["one-time", "monthly", "quarterly", "yearly"] as const;
const DEFAULT_PRESETS = [501, 1100, 2100, 5100, 11000, 25000];

type Cause = {
  kind: Kind;
  id: string;
  title: string;
  description: string | null;
  image: string | null;
  target?: number;
  collected?: number;
  suggested?: number[];
  min_amount?: number;
  is_80g?: boolean;
  allow_recurring?: boolean;
  category_id?: string | null;
  scheme_id?: number;
  donation_type?: string;
  recognition_applicable?: boolean;
  recognition_text?: string | null;
  date_required?: boolean;
  occasion_required?: boolean;
  partial_allowed?: boolean;
};

/** General (non-campaign) donation — paid via the same donations API without a scheme_id. */
const GENERAL_CAUSE: Cause = {
  kind: "general",
  id: "general",
  title: "General Donation",
  description:
    "Support Jivandhara where it is needed most — shelter, food, medical care and daily seva for our elders.",
  image: generalDonationImage,
  suggested: DEFAULT_PRESETS,
  min_amount: 100,
  is_80g: true,
  allow_recurring: false,
  donation_type: "General Donation",
};

export default function CauseDetail() {
  const { kind, id } = useParams({ from: "/donate/c/$kind/$id" });
  return <CheckoutFlow kind={kind} id={id} />;
}

export function GeneralDonationCheckout() {
  return <CheckoutFlow kind="general" id="general" />;
}

function CheckoutFlow({ kind, id }: { kind: string; id: string }) {
  const nav = useNavigate();
  const checkoutPath =
    kind === "general" ? "/donate/general" : `/donate/c/${kind}/${id}`;
  const [cause, setCause] = useState<Cause | null>(null);
  // Campaigns and general donations are paid online through the donations API.
  const isApiCause = cause?.kind === "campaign" || cause?.kind === "general";
  const [loading, setLoading] = useState(true);

  // Wizard state
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [amountInput, setAmountInput] = useState("1100");
  const amount = Number(amountInput) || 0;
  const [frequency, setFrequency] = useState<string>("one-time");
  const [donor, setDonor] = useState<DonorInfo>(emptyDonor);
  const [payment] = useState<PaymentInfo>(emptyPayment);
  const [extra, setExtra] = useState({
    in_memory_of: "",
    occasion: "",
    anonymous: false,
    want_80g: true,
    remarks: "",
    recognition_name: "",
    family_display_name: "",
    seva_date: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [payOnline, setPayOnline] = useState(true);
  const [location, setLocation] = useState<DonationLocationValue>({
    district_id: "",
    city_id: "",
    area_id: "",
  });
  const [verifying, setVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] =
    useState<PaymentVerificationStatus>("idle");
  const [useHeadDonorFlow, setUseHeadDonorFlow] = useState(false);
  const [headSearchPhone, setHeadSearchPhone] = useState("");
  const [headSearchPhoneCountry, setHeadSearchPhoneCountry] =
    useState<PhoneCountryCode>(DEFAULT_PHONE_COUNTRY);
  const [searchingHeadDonor, setSearchingHeadDonor] = useState(false);
  const [fetchedDonorId, setFetchedDonorId] = useState<number | null>(null);
  const [isAlreadyHead, setIsAlreadyHead] = useState(false);
  const [headPhone, setHeadPhone] = useState("");
  const [headPhoneCountry, setHeadPhoneCountry] = useState<PhoneCountryCode>(
    DEFAULT_PHONE_COUNTRY
  );
  const [searchingHead, setSearchingHead] = useState(false);
  const [headRecord, setHeadRecord] = useState<DonationDonorRecord | null>(
    null
  );
  const [selectedChildId, setSelectedChildId] = useState<number | null>(null);
  const [addingNewChild, setAddingNewChild] = useState(false);
  const [donorErrors, setDonorErrors] = useState<DonorFieldErrors>({});
  const startCampaignPayment = useCampaignRazorpayPayment();

  const resetHeadFlow = () => {
    setUseHeadDonorFlow(false);
    setHeadSearchPhone("");
    setHeadSearchPhoneCountry(DEFAULT_PHONE_COUNTRY);
    setHeadPhone("");
    setHeadPhoneCountry(DEFAULT_PHONE_COUNTRY);
    setHeadRecord(null);
    setSelectedChildId(null);
    setAddingNewChild(false);
    setIsAlreadyHead(false);
    setFetchedDonorId(null);
    setDonor(emptyDonor);
    setLocation({ district_id: "", city_id: "", area_id: "" });
  };

  const applyHeadSearchResult = (
    record: DonationDonorRecord,
    phone: string
  ) => {
    const mappedHead = mapDonorRecordToDonorInfo(record);
    setHeadRecord(record);
    setHeadPhone(phone);
    setIsAlreadyHead(true);
    setSelectedChildId(null);
    setAddingNewChild(false);
    setFetchedDonorId(record.id);
    setDonor(mappedHead.donor);
    setLocation(mappedHead.location);
    setDonorErrors({});
  };

  const selectHeadChild = (child: DonationDonorRecord) => {
    const mapped = mapDonorRecordToDonorInfo(child);
    setSelectedChildId(child.id);
    setAddingNewChild(false);
    setFetchedDonorId(child.id);
    setDonor(mapped.donor);
    setLocation(mapped.location);
    setDonorErrors({});
  };

  const startAddingNewChild = () => {
    if (!headRecord) {
      return;
    }
    setIsAlreadyHead(true);
    setHeadPhone(String(headRecord.phone ?? headPhone));
    setAddingNewChild(true);
    setSelectedChildId(null);
    setFetchedDonorId(null);
    setDonor(emptyDonor);
    setLocation({ district_id: "", city_id: "", area_id: "" });
    setDonorErrors({});
  };

  const handleDonorChange = (next: DonorInfo) => {
    setDonor(next);
    setDonorErrors(prev => {
      const updated = { ...prev };
      (Object.keys(next) as (keyof DonorInfo)[]).forEach(key => {
        if (next[key] !== donor[key]) {
          delete updated[key];
        }
      });
      return updated;
    });
  };

  const handleLocationChange = (next: DonationLocationValue) => {
    setLocation(next);
    setDonorErrors(prev => {
      const updated = { ...prev };
      if (next.district_id !== location.district_id) {
        delete updated.district;
      }
      if (next.city_id !== location.city_id) {
        delete updated.city;
      }
      if (next.area_id !== location.area_id) {
        delete updated.area;
      }
      return updated;
    });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      if (kind === "general") {
        setCause(GENERAL_CAUSE);
      } else if (kind === "campaign") {
        const scheme = await publicDonationSchemeService.getScheme(id);
        if (scheme) {
          setCause(schemeToCheckoutCause(scheme));
        }
      } else if (kind === "type") {
        const { data } = await supabase
          .from("donation_types")
          .select("*")
          .eq("id", id)
          .maybeSingle();
        if (data) {
          setCause({
            kind: "type",
            id: data.id,
            title: dbText(data, "name"),
            description: dbNullableText(data, "description"),
            image: dbNullableText(data, "image_url"),
            suggested: dbNumberList(data, "suggested_amounts").length
              ? dbNumberList(data, "suggested_amounts")
              : DEFAULT_PRESETS,
            min_amount: dbNumber(data, "min_amount", 100) || 100,
            is_80g: dbBool(data, "is_80g_applicable"),
            allow_recurring: dbBool(data, "is_recurring_allowed"),
          });
        }
      } else if (kind === "category") {
        const { data } = await supabase
          .from("donation_categories")
          .select("*")
          .eq("id", id)
          .maybeSingle();
        if (data) {
          setCause({
            kind: "category",
            id: data.id,
            title: dbText(data, "name"),
            description: dbNullableText(data, "description"),
            image: dbNullableText(data, "banner_url"),
            target: dbNumber(data, "target_amount") || undefined,
            suggested: dbNumberList(data, "suggested_amounts").length
              ? dbNumberList(data, "suggested_amounts")
              : DEFAULT_PRESETS,
            min_amount: 100,
            is_80g: true,
            allow_recurring: false,
          });
        }
      } else if (kind === "infrastructure") {
        const { data } = await supabase
          .from("infrastructure_items")
          .select("*")
          .eq("id", id)
          .maybeSingle();
        if (data) {
          const requiredAmount = dbNumber(data, "required_amount");
          setCause({
            kind: "infrastructure",
            id: data.id,
            title: dbText(data, "title"),
            description:
              dbNullableText(data, "full_description") ??
              dbNullableText(data, "description"),
            image: dbNullableText(data, "image_url"),
            target: requiredAmount,
            collected: dbNumber(data, "collected_amount"),
            suggested: dbBool(data, "partial_allowed")
              ? [11000, 25000, 51000, 101000, requiredAmount]
              : [requiredAmount],
            min_amount: dbBool(data, "partial_allowed") ? 1000 : requiredAmount,
            is_80g: true,
            partial_allowed: dbBool(data, "partial_allowed"),
            recognition_applicable: dbBool(data, "recognition_applicable"),
            recognition_text: dbNullableText(data, "recognition_text"),
            category_id: dbNullableText(data, "category_id"),
          });
        }
      } else if (kind === "seva") {
        const { data } = await supabase
          .from("seva_options")
          .select("*")
          .eq("id", id)
          .maybeSingle();
        if (data) {
          const fixed =
            dbText(data, "amount_type") === "fixed" &&
            dbNumber(data, "fixed_amount")
              ? [dbNumber(data, "fixed_amount")]
              : null;
          setCause({
            kind: "seva",
            id: data.id,
            title: dbText(data, "title"),
            description: dbNullableText(data, "description"),
            image: dbNullableText(data, "image_url"),
            suggested:
              fixed ??
              (dbNumberList(data, "suggested_amounts").length
                ? dbNumberList(data, "suggested_amounts")
                : DEFAULT_PRESETS),
            min_amount: dbNumber(data, "min_amount", 100) || 100,
            is_80g: true,
            date_required: dbBool(data, "date_required"),
            occasion_required: dbBool(data, "occasion_required"),
            category_id: dbNullableText(data, "category_id"),
          });
        }
      } else if (kind === "event") {
        const { data } = await supabase
          .from("events")
          .select("*")
          .eq("id", id)
          .maybeSingle();
        if (data) {
          setCause({
            kind: "event",
            id: data.id,
            title: dbText(data, "title"),
            description:
              dbNullableText(data, "short_description") ??
              dbNullableText(data, "full_description"),
            image: dbNullableText(data, "banner_url"),
            target: dbNumber(data, "goal_amount"),
            collected: dbNumber(data, "raised_amount"),
            suggested: dbNumberList(data, "donation_amount_options").length
              ? dbNumberList(data, "donation_amount_options")
              : DEFAULT_PRESETS,
            min_amount: 100,
            is_80g: true,
          });
        }
      }
      setLoading(false);
    })();
  }, [kind, id]);

  useEffect(() => {
    if (cause?.suggested?.length) {
      setAmountInput(String(cause.suggested[0]));
    }
    if (cause) {
      setExtra(p => ({ ...p, want_80g: !!cause.is_80g }));
    }
  }, [cause]);

  const handleStep1HeadSearch = async () => {
    const phone = headSearchPhone.trim();
    if (!isValidMobileNumber(phone, headSearchPhoneCountry)) {
      return toast.error(
        "Please enter a valid head mobile number for the selected country"
      );
    }

    setSearchingHeadDonor(true);
    try {
      const record = await publicDonationDonorService.searchByPhone(phone);
      if (!record) {
        return toast.info("No head donor found for this mobile number");
      }

      applyHeadSearchResult(record, phone);
      toast.success("Head donor found. Details have been pre-filled.");
      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Error searching head donor");
    } finally {
      setSearchingHeadDonor(false);
    }
  };

  const handleHeadSearch = async () => {
    const phone = headPhone.trim();
    if (!isValidMobileNumber(phone, headPhoneCountry)) {
      return toast.error(
        "Please enter a valid head mobile number for the selected country"
      );
    }

    setSearchingHead(true);
    try {
      const record = await publicDonationDonorService.searchByPhone(phone);
      if (!record) {
        setHeadRecord(null);
        setSelectedChildId(null);
        setFetchedDonorId(null);
        return toast.info("No head donor found for this mobile number");
      }
      applyHeadSearchResult(record, phone);
      toast.success("Head donor found.");
    } catch {
      toast.error("Error searching head donor");
    } finally {
      setSearchingHead(false);
    }
  };

  const syncDonorMasterAfterPayment = async () => {
    if (!cause || !isApiCause) {
      return;
    }

    const payloadArgs = {
      donationType: cause.donation_type ?? cause.title,
      donor,
      location,
      amount,
      payOnline,
      payment,
      message: extra.remarks,
      headStatus: { isAlreadyHead, headPhone },
      wants80gReceipt: extra.want_80g,
      keepAnonymousOnDonorWall: extra.anonymous,
    };

    if (fetchedDonorId) {
      await publicDonationDonorService.update(
        fetchedDonorId,
        buildPublicCheckoutDonorPatch(payloadArgs)
      );
      return;
    }

    await publicDonationDonorService.create(
      buildPublicCheckoutDonorCreate(payloadArgs)
    );
  };

  const { isAuthenticated } = useAuth();

  const goNext = () => {
    if (!isAuthenticated) {
      toast.info("Please log in to continue with your donation");
      nav({
        to: "/login",
        search: { redirectTo: checkoutPath },
      });
      return;
    }
    if (step === 1) {
      const parsedAmount = Number(amountInput);
      if (
        !amountInput.trim() ||
        !Number.isFinite(parsedAmount) ||
        parsedAmount < (cause?.min_amount ?? 100)
      ) {
        return toast.error(
          `Minimum amount is ${inr(cause?.min_amount ?? 100)}`
        );
      }
      if (cause?.kind === "seva" && cause?.date_required && !extra.seva_date) {
        return toast.error("Please choose a seva date");
      }
      if (useHeadDonorFlow && !headRecord) {
        return toast.error("Please search head donor mobile number first");
      }
      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (step === 2) {
      const validation = validateCheckoutDonorDetails({
        donor,
        location,
        isCampaign: isApiCause,
        want80g: extra.want_80g,
      });
      if (validation.message) {
        setDonorErrors(validation.errors);
        return toast.error(validation.message);
      }
      setDonorErrors({});
      if (
        isAlreadyHead &&
        !headRecord &&
        !isValidMobileNumber(headPhone.trim(), headPhoneCountry)
      ) {
        return toast.error(
          "Please enter a valid head mobile number for the selected country"
        );
      }
      if (isAlreadyHead && !headRecord) {
        return toast.error("Please search and verify the head donor first");
      }
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const submit = async () => {
    if (!cause) {
      return;
    }
    if (!isAuthenticated) {
      toast.info("Please log in to complete your donation");
      nav({
        to: "/login",
        search: { redirectTo: checkoutPath },
      });
      return;
    }
    setSubmitting(true);

    try {
      if (isApiCause && payOnline) {
        const validation = validateCheckoutDonorDetails({
          donor,
          location,
          isCampaign: true,
          want80g: extra.want_80g,
        });
        if (validation.message) {
          setDonorErrors(validation.errors);
          throw new Error(validation.message);
        }
        setDonorErrors({});

        await startCampaignPayment({
          payload: {
            ...(cause.kind === "campaign"
              ? { scheme_id: cause.scheme_id ?? Number(cause.id) }
              : {}),
            name: donor.name.trim(),
            email: donor.email.trim(),
            phone: donor.mobile.trim(),
            organization: donor.company.trim() || undefined,
            donation_type: cause.donation_type ?? cause.title,
            amount: Number(amount).toFixed(2),
            mode: "online",
            payment_mode: "upi",
            pan_number: donor.pan.trim() || undefined,
            message: extra.remarks.trim() || undefined,
            address_line1: donor.address.trim(),
            district: Number(location.district_id),
            city: Number(location.city_id),
            area: Number(location.area_id),
            pincode: donor.pincode.trim(),
            is_head: !isAlreadyHead,
            is_already_head: isAlreadyHead,
            head_phone: isAlreadyHead ? headPhone.trim() : undefined,
            wants_80g_receipt: extra.want_80g,
            keep_anonymous_on_donor_wall: extra.anonymous,
          },
          description: cause.title,
          onVerifyStart: () => {
            setVerifying(true);
            setVerificationStatus("verifying");
          },
          onVerifySuccess: async () => {
            try {
              await syncDonorMasterAfterPayment();
            } catch {
              toast.error(
                "Payment succeeded but donor record could not be saved. Please contact support."
              );
            }
            setVerificationStatus("success");
            await new Promise(resolve => setTimeout(resolve, 1500));
            setVerifying(false);
            setVerificationStatus("idle");
            setSubmitting(false);
            nav({ to: "/donate" });
          },
          onVerifyError: () => {
            setVerifying(false);
            setVerificationStatus("idle");
            setSubmitting(false);
          },
          onDismiss: () => {
            toast.message("Payment cancelled. You can try again when ready.");
            setSubmitting(false);
          },
        });
        return;
      }

      let donorId: string | null = null;
      if (donor.mobile) {
        const { data: existing } = await supabase
          .from("donors")
          .select("id")
          .eq("mobile", donor.mobile)
          .maybeSingle();
        if (existing) {
          donorId = existing.id;
          await supabase
            .from("donors")
            .update({
              full_name: donor.name,
              email: donor.email || null,
              pan: donor.pan || null,
              address: donor.address || null,
              city: donor.city || null,
              state: donor.state || null,
              pincode: donor.pincode || null,
              country: donor.country || null,
            })
            .eq("id", donorId);
        } else {
          const { data: created, error } = await supabase
            .from("donors")
            .insert({
              full_name: donor.name,
              mobile: donor.mobile,
              alt_mobile: donor.alt_mobile || null,
              email: donor.email || null,
              pan: donor.pan || null,
              aadhaar: donor.aadhaar || null,
              date_of_birth: donor.date_of_birth || null,
              address: donor.address || null,
              city: donor.city || null,
              state: donor.state || null,
              pincode: donor.pincode || null,
              country: donor.country || "India",
              csr_eligibility: donor.csr_eligibility,
              occupation: donor.occupation || null,
              company: donor.company || null,
              family_name: donor.family_name || null,
            })
            .select("id")
            .single();
          if (error || !created) {
            throw error ?? new Error("Could not create donor record.");
          }
          donorId = created.id;
        }
      }

      const { data: don, error: donErr } = await supabase
        .from("donations")
        .insert({
          donor_id: donorId,
          amount,
          donation_date: new Date().toISOString().slice(0, 10),
          donation_type_id: cause.kind === "type" ? cause.id : null,
          donation_category_id:
            cause.kind === "category" ? cause.id : (cause.category_id ?? null),
          campaign_id: cause.kind === "campaign" ? cause.id : null,
          event_id: cause.kind === "event" ? cause.id : null,
          infrastructure_item_id:
            cause.kind === "infrastructure" ? cause.id : null,
          seva_id: cause.kind === "seva" ? cause.id : null,
          frequency,
          purpose: cause.title,
          category: cause.title,
          in_memory_of: extra.in_memory_of || null,
          occasion: extra.occasion || null,
          is_anonymous: extra.anonymous,
          requires_80g: extra.want_80g,
          recognition_name:
            cause.recognition_applicable && extra.recognition_name
              ? extra.recognition_name
              : null,
          family_display_name: extra.family_display_name || null,
          seva_date:
            cause.kind === "seva" && extra.seva_date ? extra.seva_date : null,
          payment_mode: payOnline
            ? "razorpay"
            : payment.payment_mode || "pending",
          transaction_id: payOnline ? null : payment.transaction_id || null,
          cheque_no: payOnline ? null : payment.cheque_no || null,
          bank_name: payOnline ? null : payment.bank_name || null,
          payment_status: "pending",
          remarks: extra.remarks || null,
        })
        .select("id,receipt_no")
        .single();
      if (donErr || !don) {
        throw donErr ?? new Error("Could not record donation.");
      }

      // Create recurring plan when donor opted for a recurring frequency
      if (donorId && frequency && frequency !== "one-time") {
        const nextDate = new Date();
        if (frequency === "monthly") {
          nextDate.setMonth(nextDate.getMonth() + 1);
        } else if (frequency === "quarterly") {
          nextDate.setMonth(nextDate.getMonth() + 3);
        } else if (frequency === "yearly") {
          nextDate.setFullYear(nextDate.getFullYear() + 1);
        }
        await supabase.from("recurring_donations").insert({
          donor_id: donorId,
          amount,
          frequency,
          donation_type_id: cause.kind === "type" ? cause.id : null,
          campaign_id: cause.kind === "campaign" ? cause.id : null,
          start_date: new Date().toISOString().slice(0, 10),
          next_payment_date: nextDate.toISOString().slice(0, 10),
          status: "active",
          notes: `Auto-created from ${cause.title}`,
        });
      }

      if (payOnline) {
        toast.error(
          "Online payment is currently available only for active campaigns."
        );
      } else {
        toast.success(
          "Donation recorded. Our team will confirm your offline payment."
        );
        nav({
          to: "/donate/thank-you/$ref",
          params: { ref: dbText(don, "receipt_no", "pending") },
        });
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Could not submit donation");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageShell>
        <div className="grid min-h-[40vh] place-items-center">
          <Loader2 className="h-6 w-6 animate-spin text-gold" />
        </div>
      </PageShell>
    );
  }

  if (!cause) {
    return (
      <PageShell>
        <div className="mx-auto max-w-2xl px-4 py-20 text-center">
          <h1 className="font-serif text-2xl font-bold text-primary">
            Cause not found
          </h1>
          <p className="mt-2 text-muted-foreground">
            The cause you’re looking for may have ended.
          </p>
          <Button asChild className="mt-6">
            <Link to="/donate">Browse all causes</Link>
          </Button>
        </div>
      </PageShell>
    );
  }

  const pct = cause.target
    ? Math.min(100, ((cause.collected ?? 0) / cause.target) * 100)
    : 0;
  const remaining = Math.max(0, (cause.target ?? 0) - (cause.collected ?? 0));
  const presets = cause.suggested?.length ? cause.suggested : DEFAULT_PRESETS;

  return (
    <PageShell>
      <PaymentVerificationOverlay
        open={verifying}
        status={verificationStatus}
      />
      {/* HERO */}
      <section className="relative isolate overflow-hidden text-primary-foreground">
        {/* Rich multi-stop gradient using logo colors (indigo disc → saffron → leaf) */}
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(120% 80% at 0% 0%, oklch(0.42 0.14 250) 0%, transparent 55%)," +
              "radial-gradient(100% 80% at 100% 100%, oklch(0.55 0.18 55 / 0.85) 0%, transparent 55%)," +
              "radial-gradient(90% 70% at 50% 120%, oklch(0.48 0.14 145 / 0.55) 0%, transparent 60%)," +
              "linear-gradient(135deg, oklch(0.28 0.10 250) 0%, oklch(0.32 0.13 255) 50%, oklch(0.36 0.14 260) 100%)",
          }}
        />
        <div
          className="absolute inset-0 -z-10 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 20%, white 1px, transparent 1px), radial-gradient(circle at 75% 60%, white 1px, transparent 1px)",
            backgroundSize: "60px 60px, 90px 90px",
          }}
        />
        <div className="mx-auto max-w-6xl px-4 py-10">
          <Button
            asChild
            variant="ghost"
            className="text-primary-foreground/80 hover:bg-primary-foreground/10 -ml-3 mb-4 hover:text-primary-foreground"
          >
            <Link to="/donate">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to all causes
            </Link>
          </Button>
          <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr] lg:items-center">
            <div>
              <span
                className="bg-gold/20 anim-fade-up inline-block rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gold"
                style={{ animationDelay: "100ms" }}
              >
                {cause.kind === "campaign"
                  ? "Active Campaign"
                  : cause.kind === "general"
                    ? "Donate to Jivandhara"
                    : cause.kind === "type"
                      ? "Way to Donate"
                      : "Cause"}
              </span>
              <h1 className="mt-3 font-serif text-3xl font-bold leading-tight md:text-4xl lg:text-5xl">
                <span className="anim-line" style={{ animationDelay: "220ms" }}>
                  <span>{cause.title}</span>
                </span>
              </h1>
              {cause.description && (
                <p
                  className="text-primary-foreground/85 anim-fade-up mt-3 max-w-xl"
                  style={{ animationDelay: "380ms" }}
                >
                  {cause.description}
                </p>
              )}
              {typeof cause.target === "number" && cause.target > 0 && (
                <div
                  className="border-primary-foreground/20 bg-primary-foreground/10 anim-fade-up mt-5 rounded-2xl border p-4 backdrop-blur"
                  style={{ animationDelay: "500ms" }}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="font-serif text-2xl font-bold">
                      {inr(cause.collected ?? 0)}
                    </span>
                    <span className="text-primary-foreground/75 text-sm">
                      of {inr(cause.target)}
                    </span>
                  </div>
                  <div className="bg-primary-foreground/15 mt-2 h-2 w-full overflow-hidden rounded-full">
                    <div
                      className="to-gold/70 h-full rounded-full bg-gradient-to-r from-gold"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="mt-2 flex justify-between text-xs">
                    <span className="font-semibold text-gold">
                      {pct.toFixed(0)}% funded
                    </span>
                    <span className="text-primary-foreground/75">
                      {inr(remaining)} still needed
                    </span>
                  </div>
                </div>
              )}
            </div>
            {cause.image && (
              <div
                className="border-primary-foreground/15 anim-zoom-in overflow-hidden rounded-2xl border shadow-xl"
                style={{ animationDelay: "350ms" }}
              >
                <img
                  src={cause.image}
                  alt={cause.title}
                  className="h-64 w-full object-cover lg:h-72"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* WIZARD */}
      <section className="mx-auto max-w-6xl px-4 py-10">
        <Stepper step={step} />
        <div className="mt-6 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          <div
            className="anim-fade-up rounded-2xl border border-border bg-card p-6 shadow-sm"
            style={{ animationDelay: "550ms" }}
          >
            <div key={step} className="anim-fade">
              {step === 1 && (
                <StepAmount
                  cause={cause}
                  presets={presets}
                  amountInput={amountInput}
                  setAmountInput={setAmountInput}
                  min={cause.min_amount ?? 100}
                  allowRecurring={!!cause.allow_recurring}
                  frequency={frequency}
                  setFrequency={setFrequency}
                  extra={extra}
                  setExtra={setExtra}
                  isAlreadyDonor={useHeadDonorFlow}
                  setIsAlreadyDonor={(checked: boolean) => {
                    if (checked) {
                      setUseHeadDonorFlow(true);
                    } else {
                      resetHeadFlow();
                    }
                  }}
                  donorSearchPhone={headSearchPhone}
                  setDonorSearchPhone={setHeadSearchPhone}
                  donorSearchPhoneCountry={headSearchPhoneCountry}
                  setDonorSearchPhoneCountry={setHeadSearchPhoneCountry}
                  searchingDonor={searchingHeadDonor}
                  onDonorSearch={handleStep1HeadSearch}
                  onClearFetchedDonor={resetHeadFlow}
                />
              )}
              {step === 2 && (
                <div className="space-y-5">
                  <h2 className="font-serif text-xl font-bold text-primary">
                    Your details
                  </h2>

                  {headRecord ? (
                    <HeadDonorChildrenSection
                      head={headRecord}
                      selectedChildId={selectedChildId}
                      isAddingNewChild={addingNewChild}
                      onSelectChild={selectHeadChild}
                      onAddNewChild={startAddingNewChild}
                    />
                  ) : (
                    <div>
                      <Label>Head Status</Label>
                      <RadioGroup
                        value={isAlreadyHead ? "already_head" : "head"}
                        onValueChange={value => {
                          const alreadyHead = value === "already_head";
                          setIsAlreadyHead(alreadyHead);
                          if (!alreadyHead) {
                            setHeadPhone("");
                            setHeadRecord(null);
                            setSelectedChildId(null);
                            setFetchedDonorId(null);
                          }
                        }}
                        className="border-border/70 mt-2 grid overflow-hidden rounded-xl border bg-white sm:grid-cols-2 sm:divide-x sm:divide-border"
                      >
                        <label className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-medium">
                          <RadioGroupItem value="head" />I am the Family Head
                        </label>
                        <label className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-medium">
                          <RadioGroupItem value="already_head" />A Family Head
                          Already Exists
                        </label>
                      </RadioGroup>
                      {isAlreadyHead && (
                        <div className="mt-3">
                          <Label data-required="true">Head Mobile Number</Label>
                          <div className="mt-1 flex gap-2">
                            <MobileNumberField
                              className="min-w-0 flex-1"
                              value={headPhone}
                              country={headPhoneCountry}
                              onCountryChange={code => {
                                setHeadPhoneCountry(code);
                                setHeadPhone(
                                  sanitizeMobileNumber(headPhone, code)
                                );
                                setHeadRecord(null);
                                setSelectedChildId(null);
                                setFetchedDonorId(null);
                              }}
                              onChange={value => {
                                setHeadPhone(value);
                                setHeadRecord(null);
                                setSelectedChildId(null);
                                setFetchedDonorId(null);
                              }}
                              placeholder="Head mobile"
                            />
                            <Button
                              type="button"
                              onClick={handleHeadSearch}
                              disabled={searchingHead}
                            >
                              {searchingHead ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Search className="h-4 w-4" />
                              )}
                              Search
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <DonorFields
                    value={donor}
                    onChange={handleDonorChange}
                    errors={donorErrors}
                    requireEmail={isApiCause}
                    requireAddress={isApiCause}
                    requirePincode={isApiCause}
                    requirePan={extra.want_80g}
                    lockMobile={fetchedDonorId !== null}
                  />
                  {isApiCause && (
                    <DonationLocationFields
                      value={location}
                      onChange={handleLocationChange}
                      errors={donorErrors}
                    />
                  )}
                  {cause.is_80g && (
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={extra.want_80g}
                        onChange={e =>
                          setExtra({ ...extra, want_80g: e.target.checked })
                        }
                      />
                      I would like an 80G tax-exemption receipt (PAN required).
                    </label>
                  )}
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={extra.anonymous}
                      onChange={e =>
                        setExtra({ ...extra, anonymous: e.target.checked })
                      }
                    />
                    Keep my donation anonymous on the donor wall.
                  </label>
                </div>
              )}
              {step === 3 && (
                <div className="space-y-5">
                  <h2 className="font-serif text-xl font-bold text-primary">
                    Payment
                  </h2>
                  <div className="grid gap-3">
                    <button
                      type="button"
                      onClick={() => setPayOnline(true)}
                      className={`rounded-xl border-2 p-4 text-left transition ${payOnline ? "bg-gold/10 border-gold" : "hover:border-gold/50 border-border"}`}
                    >
                      <div className="flex items-center gap-2 font-semibold text-primary">
                        <span className="grid h-5 w-5 place-items-center rounded-full border-2 border-gold">
                          {payOnline && (
                            <span className="h-2 w-2 rounded-full bg-gold" />
                          )}
                        </span>
                        Pay Online (Recommended)
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        UPI · Cards · Netbanking · Wallets. Instant receipt.
                      </p>
                    </button>
                    {/* Pay Offline temporarily hidden
                    <button
                      type="button"
                      onClick={() => setPayOnline(false)}
                      className={`rounded-xl border-2 p-4 text-left transition ${!payOnline ? "bg-gold/10 border-gold" : "hover:border-gold/50 border-border"}`}
                    >
                      <div className="flex items-center gap-2 font-semibold text-primary">
                        <span className="grid h-5 w-5 place-items-center rounded-full border-2 border-gold">
                          {!payOnline && (
                            <span className="h-2 w-2 rounded-full bg-gold" />
                          )}
                        </span>
                        Pay Offline
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        UPI transfer / Bank / Cheque. Verified within 24 hrs.
                      </p>
                    </button>
                    */}
                  </div>
                  {/* Offline payment fields temporarily hidden
                  {!payOnline && (
                    <PaymentFields value={payment} onChange={setPayment} />
                  )}
                  */}
                  <div>
                    <Label>Remarks / Special requests</Label>
                    <Textarea
                      rows={3}
                      value={extra.remarks}
                      onChange={e =>
                        setExtra({ ...extra, remarks: e.target.value })
                      }
                    />
                  </div>
                  <div className="border-gold/30 bg-gold/5 rounded-xl border p-4 text-sm text-muted-foreground">
                    You will be redirected to Razorpay&apos;s secure checkout.
                    After payment your 80G receipt is generated instantly.
                    {/* Offline message temporarily hidden
                    {payOnline
                      ? "You will be redirected to Razorpay's secure checkout. After payment your 80G receipt is generated instantly."
                      : "Our team will verify your offline payment and email you an official 80G receipt within 24 hours."}
                    */}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-between gap-3 border-t border-border pt-5">
              {step > 1 ? (
                <Button
                  variant="outline"
                  onClick={() => setStep(s => (s - 1) as 1 | 2 | 3)}
                  className="gap-1"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
              ) : (
                <span />
              )}
              {step < 3 ? (
                <Button
                  onClick={goNext}
                  className="hover:bg-gold/90 gap-1 bg-gold text-gold-foreground"
                >
                  Continue <ArrowRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={submitting || verifying}
                  size="lg"
                  className="hover:bg-gold/90 gap-2 bg-gold text-gold-foreground"
                >
                  <Heart className="h-4 w-4" />
                  {submitting ? "Submitting…" : `Donate ${inr(amount)}`}
                </Button>
              )}
            </div>
          </div>

          {/* Sidebar summary */}
          <aside
            className="anim-fade-up space-y-4"
            style={{ animationDelay: "680ms" }}
          >
            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Donation summary
              </div>
              <div className="mt-1 font-serif text-base font-bold text-primary">
                {cause.title}
              </div>
              <div className="mt-3 flex items-baseline justify-between border-t border-border pt-3">
                <span className="text-sm text-muted-foreground">Amount</span>
                <span className="font-serif text-2xl font-bold text-primary">
                  {inr(amount)}
                </span>
              </div>
              {cause.allow_recurring && frequency !== "one-time" && (
                <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                  <span>Frequency</span>
                  <span className="font-semibold capitalize">{frequency}</span>
                </div>
              )}
              <ul className="mt-4 space-y-2 text-sm">
                <Bullet ok={step >= 1} label="Cause & amount" />
                <Bullet ok={step >= 2} label="Your details" />
                <Bullet ok={step >= 3} label="Payment" />
              </ul>
            </div>
            <TrustCard is80g={!!cause.is_80g} />
            <PaymentInfoCard />
          </aside>
        </div>
      </section>
    </PageShell>
  );
}

function Stepper({ step }: { step: 1 | 2 | 3 }) {
  const steps = [
    { n: 1, label: "Amount" },
    { n: 2, label: "Your Details" },
    { n: 3, label: "Payment" },
  ];
  return (
    <div className="flex items-center justify-center gap-2 md:gap-4">
      {steps.map((s, i) => (
        <div key={s.n} className="flex items-center gap-2 md:gap-4">
          <div
            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${step >= s.n ? "bg-gold/10 border-gold text-primary" : "border-border text-muted-foreground"}`}
          >
            <span
              className={`grid h-6 w-6 place-items-center rounded-full text-xs font-bold ${step >= s.n ? "bg-gold text-gold-foreground" : "bg-muted"}`}
            >
              {s.n}
            </span>
            <span className="font-semibold">{s.label}</span>
          </div>
          {i < steps.length - 1 && (
            <div
              className={`h-0.5 w-6 md:w-12 ${step > s.n ? "bg-gold" : "bg-border"}`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function StepAmount({
  cause,
  presets,
  amountInput,
  setAmountInput,
  min: _min,
  allowRecurring,
  frequency,
  setFrequency,
  extra,
  setExtra,
  isAlreadyDonor,
  setIsAlreadyDonor,
  donorSearchPhone,
  setDonorSearchPhone,
  donorSearchPhoneCountry,
  setDonorSearchPhoneCountry,
  searchingDonor,
  onDonorSearch,
  onClearFetchedDonor,
}: any) {
  const parsedAmount = Number(amountInput) || 0;
  return (
    <div className="space-y-5">
      <h2 className="font-serif text-xl font-bold text-primary">
        Choose your contribution
      </h2>
      <div>
        <Label>Select an amount (₹)</Label>
        <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-6">
          {presets.map((p: number) => (
            <button
              key={p}
              type="button"
              onClick={() => setAmountInput(String(p))}
              className={`rounded-md border p-2 text-sm font-semibold transition ${parsedAmount === p ? "bg-gold/10 border-gold text-primary" : "border-border hover:border-gold"}`}
            >
              ₹{Number(p).toLocaleString("en-IN")}
            </button>
          ))}
        </div>
        <Input
          className="mt-3"
          type="text"
          inputMode="numeric"
          value={amountInput}
          onChange={e => {
            const val = e.target.value;
            if (val === "" || /^\d+$/.test(val)) {
              setAmountInput(val);
            }
          }}
          placeholder="Other amount"
        />
      </div>

      <div className="rounded-xl border border-border bg-white p-4">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={isAlreadyDonor}
            onChange={e => {
              setIsAlreadyDonor(e.target.checked);
              if (!e.target.checked) {
                setDonorSearchPhone("");
                onClearFetchedDonor?.();
              }
            }}
          />
          A family member is already registered as a donor?
        </label>

        {isAlreadyDonor && (
          <div className="mt-4 space-y-2">
            <Label data-required="true">Family Head Mobile Number</Label>
            <div className="flex gap-2">
              <MobileNumberField
                className="min-w-0 flex-1"
                value={donorSearchPhone}
                country={donorSearchPhoneCountry}
                onCountryChange={code => {
                  setDonorSearchPhoneCountry(code);
                  setDonorSearchPhone(
                    sanitizeMobileNumber(donorSearchPhone, code)
                  );
                }}
                onChange={setDonorSearchPhone}
                placeholder="Enter the registered mobile number"
                disabled={searchingDonor}
              />
              <Button
                type="button"
                onClick={() => void onDonorSearch()}
                disabled={
                  searchingDonor
                    ? true
                    : !isValidMobileNumber(
                        donorSearchPhone,
                        donorSearchPhoneCountry
                      )
                }
                className="hover:bg-gold/90 shrink-0 gap-1 bg-gold text-gold-foreground"
              >
                {searchingDonor ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Search
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              We&apos;ll use this number to find your family and link your
              donation.
            </p>
          </div>
        )}
      </div>

      {allowRecurring && (
        <div>
          <Label>Frequency</Label>
          <Select value={frequency} onValueChange={setFrequency}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FREQUENCIES.map(f => (
                <SelectItem key={f} value={f}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        {cause?.kind === "seva" && (
          <div>
            <Label>Seva Date {cause?.date_required ? "*" : "(optional)"}</Label>
            <Input
              type="date"
              value={extra.seva_date}
              onChange={e => setExtra({ ...extra, seva_date: e.target.value })}
            />
          </div>
        )}
        {cause?.recognition_applicable && (
          <>
            <div>
              <Label>Recognition name (for plaque / donor board)</Label>
              <Input
                value={extra.recognition_name}
                onChange={e =>
                  setExtra({ ...extra, recognition_name: e.target.value })
                }
                placeholder="As it should appear"
              />
              {cause.recognition_text && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {cause.recognition_text}
                </p>
              )}
            </div>
            <div>
              <Label>Family name (optional)</Label>
              <Input
                value={extra.family_display_name}
                onChange={e =>
                  setExtra({ ...extra, family_display_name: e.target.value })
                }
                placeholder="e.g. Shah Family"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function HeadDonorChildrenSection({
  head,
  selectedChildId,
  isAddingNewChild,
  onSelectChild,
  onAddNewChild,
}: {
  head: DonationDonorRecord;
  selectedChildId: number | null;
  isAddingNewChild: boolean;
  onSelectChild: (child: DonationDonorRecord) => void;
  onAddNewChild: () => void;
}) {
  const children = head.children ?? [];

  return (
    <div className="space-y-4">
      <div className="border-primary/40 bg-primary/5 rounded-xl border px-4 py-3 text-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Head Donor
        </p>
        <div className="mt-2 flex items-center justify-between gap-4">
          <p className="font-semibold text-foreground">{head.name}</p>
          <p className="shrink-0 text-muted-foreground">
            Mobile:{" "}
            <span className="font-medium text-foreground">{head.phone}</span>
          </p>
        </div>
      </div>

      {children.length > 0 ? (
        <div>
          <Label data-required="true">Select family member</Label>
          <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <p className="text-xs text-muted-foreground sm:max-w-[70%]">
              Select an existing family member or add a new family member if
              they are not registered.
            </p>
            <Button
              type="button"
              variant="outline"
              className="border-gold/40 self-start text-primary hover:border-gold"
              onClick={onAddNewChild}
            >
              Add New Member
            </Button>
          </div>
          <RadioGroup
            value={selectedChildId ? String(selectedChildId) : ""}
            onValueChange={value => {
              const child = children.find(c => String(c.id) === value);
              if (child) {
                onSelectChild(child);
              }
            }}
            className="mt-3 grid gap-2 sm:grid-cols-2"
          >
            {children.map(child => {
              const selected = selectedChildId === child.id;
              return (
                <label
                  key={child.id}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition",
                    selected
                      ? "bg-gold/10 border-gold shadow-sm"
                      : "hover:border-gold/50 border-border bg-white"
                  )}
                >
                  <RadioGroupItem value={String(child.id)} />
                  <span className="flex min-w-0 flex-1 flex-col items-start justify-between gap-2 text-sm">
                    <span className="truncate font-semibold text-foreground">
                      {child.name}
                    </span>
                    {child.phone && (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        Mobile: {child.phone}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </RadioGroup>
          {isAddingNewChild && (
            <p className="border-gold/40 bg-gold/5 mt-3 rounded-xl border px-4 py-3 text-sm text-primary">
              Adding new child under this head. Fill all details below.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="bg-muted/20 rounded-xl border border-border px-4 py-3 text-sm text-muted-foreground">
            No family members found under this head donor. Please fill your
            details manually below.
          </p>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              className="border-gold/40 text-primary hover:border-gold"
              onClick={onAddNewChild}
            >
              Add New Child
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Bullet({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <CheckCircle2
        className={`h-4 w-4 ${ok ? "text-gold" : "text-muted-foreground/40"}`}
      />
      <span className={ok ? "text-foreground" : "text-muted-foreground"}>
        {label}
      </span>
    </li>
  );
}

function TrustCard({ is80g }: { is80g: boolean }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Why donate with us
      </div>
      <ul className="mt-3 space-y-2.5 text-sm">
        {is80g && (
          <li className="flex items-start gap-2">
            <Award className="mt-0.5 h-4 w-4 text-gold" /> 80G tax exemption
            receipt
          </li>
        )}
        <li className="flex items-start gap-2">
          <Shield className="mt-0.5 h-4 w-4 text-gold" /> 100% funds reach the
          residents
        </li>
        <li className="flex items-start gap-2">
          <Users className="mt-0.5 h-4 w-4 text-gold" /> Trusted by thousands of
          donors
        </li>
      </ul>
    </div>
  );
}
